import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { cn } from '../../utils/cn';

/**
 * Visual treatment of the tab strip.
 * - `'pill'` (default) — contained segmented-pill bar (grey surface + hairline,
 *   active tab is a raised white chip). Best for a small set of status/filter
 *   segments (All / Active / …). Unchanged from before this prop existed.
 * - `'line'` — flat underline tabs sitting on a full-width hairline, the active
 *   tab marked by a 2px accent underline. The standard look for primary
 *   page-section navigation on detail pages that carry many tabs.
 */
export type TabsVariant = 'pill' | 'line';

interface TabsContextValue {
  readonly value: string;
  readonly setValue: (v: string) => void;
  readonly baseId: string;
  readonly variant: TabsVariant;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabs(): TabsContextValue {
  const ctx = useContext(TabsContext);
  if (!ctx) throw new Error('Tabs subcomponents must be wrapped in <Tabs>');
  return ctx;
}

export interface TabsProps {
  readonly value?: string;
  readonly defaultValue: string;
  readonly onValueChange?: (v: string) => void;
  readonly children: ReactNode;
  readonly className?: string;
  /** Visual treatment of the tab strip. Defaults to `'pill'`. See {@link TabsVariant}. */
  readonly variant?: TabsVariant;
}

export function Tabs({
  value,
  defaultValue,
  onValueChange,
  children,
  className,
  variant = 'pill',
}: TabsProps) {
  const baseId = useId();
  const [internal, setInternal] = useState(defaultValue);
  const current = value ?? internal;
  const setValue = (v: string) => {
    if (value === undefined) setInternal(v);
    onValueChange?.(v);
  };
  return (
    <TabsContext.Provider value={{ value: current, setValue, baseId, variant }}>
      <div className={cn('flex flex-col gap-3', className)}>{children}</div>
    </TabsContext.Provider>
  );
}

export interface TabListProps {
  readonly children: ReactNode;
  readonly className?: string;
  readonly 'aria-label'?: string;
  /**
   * Horizontally scroll the tab strip instead of overflowing when it's wider
   * than its container (for pages with many tabs on narrow widths). Opt-in so
   * existing content-hugging tab bars are unchanged. The scrollbar is hidden;
   * native touch/trackpad/shift-wheel scrolling still works.
   */
  readonly scrollable?: boolean;
}

export function TabList({
  children,
  className,
  scrollable = false,
  'aria-label': ariaLabel,
}: TabListProps) {
  const { variant } = useTabs();
  const lineMode = variant === 'line';
  const listClassName = cn(
    lineMode
      ? 'flex gap-1 border-b border-hairline'
      : 'inline-flex gap-1 p-1 rounded-[10px] bg-surface-2 border border-hairline',
    scrollable &&
      (lineMode
        ? 'max-w-full min-w-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
        : 'flex max-w-full min-w-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'),
    className,
  );

  if (!scrollable) {
    return (
      <div role="tablist" aria-label={ariaLabel} className={listClassName}>
        {children}
      </div>
    );
  }

  return (
    <ScrollableTabList listClassName={listClassName} ariaLabel={ariaLabel}>
      {children}
    </ScrollableTabList>
  );
}

/**
 * Wraps a `scrollable` tab strip with a soft edge-fade that signals more tabs
 * exist past the edge — the strip hides its native scrollbar
 * (`[scrollbar-width:none]`), so without an affordance a user can't tell it
 * scrolls. The fade is a `mask-image` gradient (background-independent — works on
 * any palette, no chrome) applied only to the side(s) that can still scroll, so
 * it clears automatically at each end. Measured via `ResizeObserver` + `onScroll`;
 * RTL-safe (logical start/end mapped to the physical edge). Keyboard users rely
 * on the tabs' own arrow-key navigation, which scrolls the focused tab into view.
 */
function ScrollableTabList({
  children,
  listClassName,
  ariaLabel,
}: {
  readonly children: ReactNode;
  readonly listClassName: string;
  readonly ariaLabel?: string | undefined;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [canScrollStart, setCanScrollStart] = useState(false);
  const [canScrollEnd, setCanScrollEnd] = useState(false);
  const [rtl, setRtl] = useState(false);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    // `Math.abs` normalises across LTR (scrollLeft ≥ 0) and modern-browser RTL
    // (scrollLeft ≤ 0, 0 at the leading edge) — both give distance-from-start.
    const fromStart = Math.abs(el.scrollLeft);
    setCanScrollStart(fromStart > 1);
    setCanScrollEnd(maxScroll - fromStart > 1);
    setRtl(getComputedStyle(el).direction === 'rtl');
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // ResizeObserver fires once on observe → initial measure, then on every
    // container resize. Measuring in the callback (not a direct setState in the
    // effect body) keeps `react-hooks/set-state-in-effect` happy.
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);

  // Map the logical start/end to the physical left/right edge for the gradient.
  const FADE = '1.75rem';
  const leftFade = (rtl ? canScrollEnd : canScrollStart) ? FADE : '0px';
  const rightFade = (rtl ? canScrollStart : canScrollEnd) ? FADE : '0px';
  const faded = leftFade !== '0px' || rightFade !== '0px';
  const mask = `linear-gradient(to right, transparent, black ${leftFade}, black calc(100% - ${rightFade}), transparent)`;

  return (
    <div
      ref={ref}
      role="tablist"
      aria-label={ariaLabel}
      onScroll={measure}
      data-can-scroll-start={canScrollStart || undefined}
      data-can-scroll-end={canScrollEnd || undefined}
      className={listClassName}
      style={faded ? { maskImage: mask, WebkitMaskImage: mask } : undefined}
    >
      {children}
    </div>
  );
}

export interface TabProps {
  readonly value: string;
  readonly children: ReactNode;
  readonly disabled?: boolean;
  readonly className?: string;
}

export function Tab({ value, children, disabled, className }: TabProps) {
  const { value: current, setValue, baseId, variant } = useTabs();
  const lineMode = variant === 'line';
  const selected = current === value;
  const handleKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(e.key)) {
      e.preventDefault();
      const list = e.currentTarget.closest('[role="tablist"]');
      if (!list) return;
      const tabs = Array.from(
        list.querySelectorAll<HTMLButtonElement>('[role="tab"]:not([aria-disabled="true"])'),
      );
      const idx = tabs.indexOf(e.currentTarget);
      const rtl = getComputedStyle(list).direction === 'rtl';
      const forward = e.key === (rtl ? 'ArrowLeft' : 'ArrowRight');
      const next =
        e.key === 'Home'
          ? 0
          : e.key === 'End'
            ? tabs.length - 1
            : (idx + (forward ? 1 : -1) + tabs.length) % tabs.length;
      tabs[next]?.focus();
      tabs[next]?.click();
      tabs[next]?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    }
  };
  return (
    <button
      type="button"
      role="tab"
      id={`${baseId}-tab-${value}`}
      aria-controls={`${baseId}-panel-${value}`}
      aria-selected={selected}
      aria-disabled={disabled || undefined}
      tabIndex={selected ? 0 : -1}
      disabled={disabled}
      onClick={() => !disabled && setValue(value)}
      onKeyDown={handleKey}
      className={cn(
        'inline-flex shrink-0 items-center justify-center whitespace-nowrap [@media(pointer:coarse)]:min-h-11',
        'transition-[color,background-color,border-color,box-shadow] duration-(--duration-tap) ease-(--ease-out) motion-reduce:transition-none',
        'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent-text',
        lineMode
          ? cn(
              'px-3.5 py-2.5 -mb-px border-b-2 text-[13px] font-semibold focus-visible:rounded-sm',
              selected
                ? 'border-accent text-accent-text'
                : 'border-transparent text-ink-2 hover:text-ink hover:border-hairline-2',
            )
          : cn(
              'px-3 py-1.5 h-8 rounded-md text-[12.5px] font-semibold',
              selected
                ? 'bg-surface text-ink shadow-[0_1px_0_rgba(0,0,0,0.04)]'
                : 'text-ink-2 hover:text-ink',
            ),
        disabled && 'opacity-45 cursor-not-allowed',
        className,
      )}
    >
      {children}
    </button>
  );
}

export interface TabPanelProps {
  readonly value: string;
  readonly children: ReactNode;
  readonly className?: string;
  /** Keep the panel mounted even when not selected (default: unmount inactive). */
  readonly keepMounted?: boolean;
}

export function TabPanel({ value, children, className, keepMounted = false }: TabPanelProps) {
  const { value: current, baseId } = useTabs();
  const selected = current === value;
  if (!selected && !keepMounted) return null;
  return (
    <div
      role="tabpanel"
      id={`${baseId}-panel-${value}`}
      aria-labelledby={`${baseId}-tab-${value}`}
      hidden={!selected}
      className={className}
    >
      {children}
    </div>
  );
}
